<template>
  <div class="vote-container">
    <router-link to="/">
      <img src="@/assets/bobw-logo-no-name.png" alt="BoBW Logo no name" class="small-logo" />
    </router-link>

    <h2>Vote on Songs</h2>
    <p>Select a ranking (1-5) for each song. You can update your rankings anytime.</p>

    <form @submit.prevent="submitVotes">
      <div v-for="(member, index) in bandMembers" :key="index" class="member-section">
        <h3>{{ member.name }}'s Songs</h3>

        <table>
          <thead>
            <tr>
              <th>Song Title</th>
              <th>Artist</th>
              <th>Note</th>
              <th>Rank</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(song, songIndex) in member.songs" :key="songIndex">
              <td>{{ song.songTitle }}</td>
              <td>{{ song.artist }}</td>

              <!-- 🔹 Note Icon -->
              <td>
                <span
                  v-if="song.note"
                  class="note-icon hasNote"
                  @click="openNoteViewer(song.note)"
                >
                  📝
                </span>
                <span v-else class="note-icon disabled">-</span>
              </td>

              <!-- 🔹 Rank Dropdown -->
              <td>
                <select v-model="song.rank" class="rank-select">
                  <option disabled value="">Select</option>
                  <option v-for="n in 5" :key="n" :value="n">{{ n }}</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button type="submit" class="button">💾 Save Votes</button>
    </form>

    <!-- 🔹 Note Viewer Modal -->
    <div v-if="noteViewerText" class="modal-overlay">
      <div class="modal">
        <h3>Song Note</h3>
        <p class="note-text">{{ noteViewerText }}</p>
        <button class="button" @click="closeNoteViewer">Close</button>
      </div>
    </div>

    <p v-if="message" class="success">{{ message }}</p>
    <p v-if="error" class="error">{{ error }}</p>
  </div>
</template>

<script>
import { nextTick } from "vue";

export default {
  data() {
    return {
      bandMembers: [],
      message: "",
      error: "",
      noteViewerText: "",
    };
  },

  async mounted() {
    await this.loadSongs();
    await nextTick();
    await this.loadUserVotes();  // 👈 Load existing user votes AFTER songs load
  },

  methods: {
    // 🔹 Note modal
    openNoteViewer(note) {
      this.noteViewerText = note;
    },
    closeNoteViewer() {
      this.noteViewerText = "";
    },

    // 🔹 Load all songs
    async loadSongs() {
      try {
        const response = await fetch("https://zmrukyt38a.execute-api.us-west-2.amazonaws.com/v1/get-songs");
        const rawResult = await response.json();
        const result = JSON.parse(rawResult.body);

        if (!result.success) throw new Error("API returned failure");

        const membersMap = new Map();

        result.songs.forEach(song => {
          if (!membersMap.has(song.submittedBy)) {
            membersMap.set(song.submittedBy, { name: song.submittedBy, songs: [] });
          }
          membersMap.get(song.submittedBy).songs.push({
            songId: song.songId,
            songTitle: song.songTitle,
            artist: song.artist,
            note: song.note || "",
            rank: "" // Will be overridden if user has voted before
          });
        });

        this.bandMembers = Array.from(membersMap.values());

      } catch (error) {
        console.error("❌ Error loading songs:", error);
        this.error = "Failed to load songs";
      }
    },

    // 🔹 Load the user's previous votes and pre-fill ranks
    async loadUserVotes() {
      const voterUsername = localStorage.getItem("bandUser");
      if (!voterUsername) return;

      try {
        const response = await fetch(
          `https://zmrukyt38a.execute-api.us-west-2.amazonaws.com/v1/get-user-votes?voterUsername=${voterUsername}`
        );

        const result = await response.json();

        if (!result.success) return;

        const votes = result.votes; // RAW DYNAMO ITEMS

        // 🔥 MATCH RAW DYNAMODB STRUCTURE
        this.bandMembers.forEach(member => {
          member.songs.forEach(song => {
            const vote = votes.find(v => v.songId.S === song.songId);
            if (vote) {
              song.rank = Number(vote.rank.N);
            }
          });
        });

      } catch (error) {
        console.error("❌ Error loading votes:", error);
      }
    },


    // 🔹 Submit votes
    async submitVotes() {
      try {
        const voterUsername = localStorage.getItem("bandUser");
        if (!voterUsername) {
          alert("❌ Not logged in!");
          return;
        }

        // Build vote list
        const votes = [];
        this.bandMembers.forEach(member => {
          member.songs.forEach(song => {
            votes.push({
              songId: song.songId,
              voterUsername,
              rank: song.rank,
            });
          });
        });

        // Submit each vote
        for (const vote of votes) {
          await fetch("https://zmrukyt38a.execute-api.us-west-2.amazonaws.com/v1/vote-songs", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(vote),
          });
        }

        alert("✅ Votes saved!");
      } catch (error) {
        console.error("❌ Vote submit error:", error);
        alert("❌ Error submitting votes.");
      }
    },
  }
};
</script>

<style scoped>
/* 🔹 Page Container */
.vote-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #121212; /* Dark Background */
  color: white;
  text-align: center;
  padding: 20px;
}

/* 🔹 Band Member Sections */
.member-section {
  width: 100%;
  max-width: 600px;
  margin-bottom: 20px;
  background: #1e1e1e; /* Slightly lighter than background */
  padding: 15px;
  border-radius: 10px;
  box-shadow: 2px 2px 10px rgba(255, 255, 255, 0.1);
}

.small-logo {
  width: 50px;
  height: auto;
  position: absolute;
  top: 20px;
  left: 20px;
  cursor: pointer;
}

/* 🔹 Table Styling */
table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

th, td {
  padding: 10px;
  text-align: center;
  border-bottom: 1px solid #444;
}

/* 🔹 Styled Select Dropdown */
.rank-select {
  width: 100%;
  padding: 8px;
  font-size: 16px;
  background: #222;
  color: white;
  border: 1px solid #666;
  border-radius: 5px;
}

.rank-select:focus {
  outline: none;
  border-color: #e63946; /* Highlight */
}

/* Note */
.note-icon {
  font-size: 20px;
  cursor: pointer;
  opacity: 0.5;
  transition: 0.2s ease;
}

.note-icon:hover {
  opacity: 1;
}

.note-icon.hasNote {
  color: #4caf50;
  opacity: 1;
}

.note-icon.disabled {
  opacity: 0.2;
  cursor: default;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal {
  background: #1c1c1c;
  padding: 20px;
  border-radius: 10px;
  width: 300px;
  text-align: center;
  box-shadow: 0 0 15px rgba(255,255,255,0.2);
}

.note-text {
  margin: 10px 0 20px;
  white-space: pre-wrap;
}

/* 🔹 Buttons */
.button {
  padding: 12px;
  font-size: 18px;
  background: #e63946; /* Red for rock theme */
  color: white;
  border: none;
  cursor: pointer;
  width: 100%;
  max-width: 300px;
  border-radius: 5px;
  transition: 0.3s ease;
  margin-top: 15px;
}

.button:hover {
  background: #d62828;
}

/* 🔹 Success & Error Messages */
.success {
  color: #4caf50;
  font-size: 16px;
}

.error {
  color: #ff5252;
  font-size: 16px;
}
</style>
